import React from 'react'
import { Package, Users, Sparkles, Star } from 'lucide-react'
import { useTaste, PackSelection } from '../contexts/TasteContext'

interface PackTier {
  size: number
  price: number
  label: string
  description: string
  icon: React.ReactNode
  popular?: boolean
}

const PACK_TIERS: PackTier[] = [
  {
    size: 2,
    price: 50.00,
    label: '2 Tails',
    description: 'A taste of the legend',
    icon: <Package className="w-8 h-8 !text-inherit" />,
  },
  {
    size: 4,
    price: 95.00,
    label: '4 Tails',
    description: 'The ultimate holiday upgrade',
    icon: <Users className="w-8 h-8 !text-inherit" />,
  },
  {
    size: 6,
    price: 135.00,
    label: '6 Tails',
    description: 'Host an unforgettable dinner',
    icon: <Sparkles className="w-8 h-8 !text-inherit" />,
    popular: true,
  },
  {
    size: 8,
    price: 150.00,
    label: '8 Tails',
    description: 'The Labor Day cookout centerpiece',
    icon: <Star className="w-8 h-8 !text-inherit" />,
  },
]

// Show the "Only N tails left" urgency badge once the shared pool drops
// to this many tails or fewer.
const LOW_STOCK_THRESHOLD = 100

const PackSelector: React.FC = () => {
  // Inventory comes from TasteContext (single global pool, polled there) so the
  // page and selector stay in sync off one source.
  const { selectedPack, setSelectedPack, tailsRemaining, isPackSoldOut } = useTaste()

  const isSoldOut = (size: number): boolean => isPackSoldOut(size)

  const handlePackClick = (tier: PackTier) => {
    if (isSoldOut(tier.size)) return
    const pack: PackSelection = {
      size: tier.size,
      price: tier.price,
      label: tier.label,
    }
    setSelectedPack(pack)
  }

  const isSelected = (tier: PackTier) => {
    return selectedPack?.size === tier.size
  }

  return (
    <div className="mb-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold !text-white mb-3 font-heading">
          Choose Your Pack
        </h2>
        <p className="text-lg !text-white/60 max-w-2xl mx-auto">
          Flash-frozen, deep-fried lobster tails shipped directly to your door.
        </p>
      </div>

      {/* Pack Tiers Grid — 2-up on mobile so packs are compact and comparable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-6 md:gap-6">
        {PACK_TIERS.map((tier) => {
          const selected = isSelected(tier)

          return (
            <button
              key={tier.size}
              onClick={() => handlePackClick(tier)}
              disabled={isSoldOut(tier.size)}
              className={`relative flex flex-col items-center justify-center p-4 md:p-6 rounded-2xl border-3 transition-all duration-300 ${
                isSoldOut(tier.size)
                  ? 'border-gray-600 bg-gray-800/50 !text-gray-500 cursor-not-allowed opacity-60'
                  : selected
                    ? 'border-[#962831] bg-gradient-to-br from-[#962831] to-[#6B2A2A] !text-white shadow-2xl md:scale-105'
                    : 'border-white/20 hover:border-[#962831] bg-white/5 !text-white transform md:hover:scale-105 hover:shadow-2xl'
              }`}
            >
              {/* Sold Out Overlay */}
              {isSoldOut(tier.size) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl z-10">
                  <span className="text-white font-bold text-lg tracking-wider">SOLD OUT</span>
                </div>
              )}

              {/* Popular Badge — top-center ribbon so it stays on the card
                  (corner placement collided with the neighbor column at 2-up) */}
              {tier.popular && !isSoldOut(tier.size) && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2563EB] !text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap z-20">
                  POPULAR
                </div>
              )}

              {/* Icon */}
              <div className={`mb-2 md:mb-4 ${selected ? '!text-white' : '!text-[#2563EB]'}`}>
                {tier.icon}
              </div>

              {/* Pack Size */}
              <div className={`text-3xl md:text-4xl font-bold mb-1 md:mb-2 !text-[#2563EB]`}>
                {tier.size}
              </div>

              {/* Label */}
              <div className={`text-base md:text-xl font-semibold mb-1 md:mb-2 ${selected ? '!text-[#2563EB]' : '!text-white'}`}>
                {tier.label}
              </div>

              {/* Price */}
              <div className={`text-xl md:text-2xl font-bold mb-2 md:mb-3 ${selected ? '!text-white' : '!text-white'}`}>
                ${tier.price.toFixed(2)}
              </div>

              {/* Description */}
              <div className={`text-xs md:text-sm text-center ${selected ? '!text-white/90' : '!text-white/50'}`}>
                {tier.description}
              </div>

              {/* Stock Indicator — shared global pool of tails */}
              {!isSoldOut(tier.size) &&
                tailsRemaining !== null &&
                tailsRemaining > 0 &&
                tailsRemaining <= LOW_STOCK_THRESHOLD && (
                  <div className="mt-3 text-xs font-semibold !text-[#2563EB]">
                    Only {tailsRemaining} tails left
                  </div>
                )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default PackSelector
